import * as React from "react";
import * as Models from '../models';
import { Spinner } from 'reactstrap';
import { useEffect, useReducer } from "react";
import InfiniteScroll from 'react-infinite-scroller';
import { NewsService, SearchIn } from '../services/NewsService';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';

const PAGE_SIZE = 10;

type Props = {
    contentPath: string;
    cultureCode: string;
    releaseYears: number[];
    topicsList: Models.LookupItem[];
    //pdfFilePath: string;
    archivePath: string;
}

type State = {
    keywords: string;
    canLoadMore: boolean;
    isLoading: boolean;
    items: Models.NewsItem[];
    totalItems: number;
    lastFilter: SearchIn
    searchTrigger: number;
    selectedYear: number;
    selectedTopic: Models.LookupItem;
    searchInArchive: boolean;
}

const initialState: State = {

    keywords: '',
    canLoadMore: false,
    isLoading: false,
    items: [],
    totalItems: 0,
    lastFilter: undefined,
    searchTrigger: 0,
    selectedYear: 0,
    selectedTopic: undefined,
    searchInArchive: false
}

const getUrlVars = (paramName: string) => {

    let urlSearchParamString = window.location.search;
    const searchParams = new URLSearchParams((urlSearchParamString || '').toLowerCase());
    return searchParams.get(paramName) || '';
}


const NewsListing = (props: Props) => {
    const getInitialState = () => {
        let result = initialState;

        // keywords
        {
            let keywords = getUrlVars('q');
            if (keywords.length > 0)
                result.keywords = keywords;
        }

        // topic
        {
            let topicParam = getUrlVars('topic').toLowerCase();
            if (topicParam.length > 0) {
                result.selectedTopic = props.topicsList.find(x => x.title.replaceAll(' ', '-').toLowerCase() == topicParam);
            }
        }

        // year
        {
            result.selectedYear = 0;

            let year = getUrlVars('year');

            if (year.length > 0) {

                let yearInt = parseInt(year, 10);

                if (props.releaseYears.find(x => x == yearInt)) {
                    result.selectedYear = yearInt;
                }
            }
        }

        // archive
        {
            result.searchInArchive = getUrlVars('archive') == "1";
        }

        return result;
    };

    const [state, setState] = useReducer(
        (prevState: State, newState: Partial<State>) => ({ ...prevState, ...newState }),
        getInitialState()
    );

    const search = async () => {

        const lastFilter: SearchIn = {
            keywords: state.keywords,
            path: state.searchInArchive ? props.archivePath : props.contentPath,
            archiveSearch: state.searchInArchive,
            cultureCode: props.cultureCode,
            releaseYear: state.selectedYear !== 0 ? state.selectedYear : undefined,
            topic: state.selectedTopic ? state.selectedTopic.value : undefined
        };

        setState({
            isLoading: true,
            lastFilter: lastFilter
        });

        const result = await NewsService.search(lastFilter, PAGE_SIZE, 0);

        if (!result.hasErrors) {
            setState({
                items: result.value.items,
                totalItems: result.value.totalItems,
                isLoading: false,
                canLoadMore: result.value.totalItems > result.value.items.length && result.value.items.length > 0 && result.value.items.length == PAGE_SIZE
            })
        }
    }

    const loadMore = async () => {

        setState({
            isLoading: true
        });

        const result = await NewsService.search(state.lastFilter, PAGE_SIZE, state.items.length);

        if (!result.hasErrors) {

            const newItemsSet = state.items.concat(result.value.items);

            setState({
                items: newItemsSet,
                totalItems: result.value.totalItems,
                isLoading: false,
                canLoadMore: result.value.totalItems > newItemsSet.length && result.value.items.length > 0 && result.value.items.length == PAGE_SIZE
            })
        }

    }

    useEffect(() => {
        search();

    }, [state.searchTrigger, state.selectedYear, state.selectedTopic, state.searchInArchive])

    useEffect(() => {

        const dataParams = [];

        let paramsString = '';

        if (state.lastFilter) {

            const { lastFilter } = state;

            // keywords
            if (lastFilter.keywords.length > 0)
                dataParams.push('q=' + encodeURIComponent(lastFilter.keywords));

            // topics
            if (lastFilter.topic) {
                let selecteTopic = props.topicsList.find(x => x.value == lastFilter.topic);
                if (selecteTopic) {
                    dataParams.push('topic=' + encodeURIComponent(selecteTopic.title.replaceAll(' ', '-').toLowerCase()));
                }
            }

            // archive
            if (lastFilter.archiveSearch) {
                dataParams.push('archive=1');
            }
            else {
                // releas year
                if (lastFilter.releaseYear && lastFilter.releaseYear > 0) {
                    dataParams.push('year=' + lastFilter.releaseYear)
                }
            }

            paramsString += '?' + dataParams.join('&');
        }

        window.history.pushState({}, '', paramsString);

    }, [state.lastFilter])

    return (
        <React.Fragment>
            <div className="row mb-3 search-form" role="search" aria-label="Search Form">
                <div className="col  search-input-col">
                    <input
                        type="text"
                        className="form-control search-control"
                        placeholder="Keywords"
                        aria-label="Search"
                        aria-describedby="button-addon2"
                        value={state.keywords}
                        onChange={e => setState({ keywords: e.target.value })}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                search();
                            }
                        }}
                        style={{ fontStyle: "italic" }}
                    />
                </div>
                <div className="col search-buttons-col">
                    <div className="d-flex justify-content-between">
                        <UncontrolledDropdown className="me-2 flex-grow-1 dd-topics">
                            <DropdownToggle caret className="w-100">
                                {state.selectedTopic && state.selectedTopic.title ? state.selectedTopic.title : "Topics"}
                            </DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem onClick={() => setState({ selectedTopic: undefined })}>
                                    All Topics
                                </DropdownItem>
                                {props.topicsList.map((topic) => (
                                    <DropdownItem
                                        key={topic.value}
                                        onClick={() => setState({ selectedTopic: topic })}
                                    >
                                        {topic.title}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        </UncontrolledDropdown>
                        <UncontrolledDropdown className="flex-grow-1 dd-years">
                            <DropdownToggle caret className="w-100">
                                {state.searchInArchive
                                    ? "Archive"
                                    : (state.selectedYear !== 0 && state.searchInArchive ? state.selectedYear : "All years")}
                            </DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem onClick={() => setState({ selectedYear: 0, searchInArchive: false })}>
                                    All years
                                </DropdownItem>
                                {props.releaseYears.map(year => (
                                    <DropdownItem
                                        key={year}
                                        onClick={() => setState({ selectedYear: year, searchInArchive: false })}
                                    >
                                        {year}
                                    </DropdownItem>
                                ))}
                                {props.archivePath && props.archivePath.length > 0 && <DropdownItem onClick={() => setState({ searchInArchive: true, selectedYear: 0 })}>
                                    Archive
                                </DropdownItem>}

                            </DropdownMenu>
                        </UncontrolledDropdown>

                        <button
                            className="btn btn-primary flex-grow-1 btn-submit-search"
                            type="button"
                            id="button-addon2"
                            onClick={search}
                        >
                            Search
                        </button>
                    </div>
                </div>
            </div>
            <div className="results-found-container text-start m-b-md" role="alert" aria-atomic="true" aria-live="assertive">
                {state.totalItems > 0 && (
                    <span>Found <strong>{state.totalItems}</strong> result(s)</span>
                )}
                {state.totalItems === 0 && <span>No results</span>}
            </div>
            <InfiniteScroll
                pageStart={0}
                loadMore={loadMore}
                hasMore={state.canLoadMore && !state.isLoading}
                useWindow={true}
                className="news-listing-results"
            >
                {state.items.map(item => {
                    const makeAbsoluteUrl = (path: any) => {
                        if (!path) return '';
                        const baseUrl = window.location.origin;
                        return baseUrl + path.replace("~/", "/");
                    };

                    const pdfUrl = makeAbsoluteUrl(item.pdfFilePath);

                    return (
                        <div key={item.documentID} className="news-item m-b-md">
                            <div className="date-and-pills">
                                <span className="news-date m-e-sm">
                                    {new Date(item.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                </span>
                                {item.topicsList && item.topicsList.map((topicValue, index) => (
                                    <span key={index} className="topic-pill m-e-xs cursor-pointer" onClick={(e) => {
                                        e.preventDefault();
                                        setState({ selectedTopic: topicValue })
                                    }}>
                                        {topicValue.title}
                                    </span>
                                ))}
                            </div>
                            <a
                                href={pdfUrl || item.url}
                                className="latest-news-title d-flex justify-content-between text-body"
                                target={item.pdfFilePath ? "_blank" : "_self"}
                                rel={item.pdfFilePath ? "noopener noreferrer" : undefined}
                            >
                                {item.title}
                            </a>
                            {item.abstract && <div className="abstract" dangerouslySetInnerHTML={{ __html: item.abstract }}></div>}

                        </div>
                    );
                })}
            </InfiniteScroll>

            {state.isLoading &&
                <div className="text-center">
                    <Spinner color="primary" />
                </div>
            }
        </React.Fragment>
    );
}

export default NewsListing;